/* eslint-disable */

import Logger from '../../logger';
import { generateMFA } from './generateMFA';
import { verifyCustomerMFAIntegrated } from './verifyCustomerMFAIntegrated';
import { verifyCustomerAuthenticatorCodeIntegrated } from './verifyCustomerAuthenticatorCodeIntegrated';
import { checkCustomerAuthenticatorSecretExists } from './checkCustomerAuthenticatorSecretExists';
import { generateAuthenticatorSecret as generateAuthenticatorSecretService } from './generateAuthenticatorSecret';
import updateEmailService from './updateEmail';
import { verifyEmail } from './verifyEmail';
import verifyCustomerRegistration from './verifyCustomerRegistration';
import registerCustomer from './register';

interface SessionDetails {
  correlationId: string;
  brand: string;
  country: string;
  source: string;
  language: string;
  token?: string;
  shouldLinkAccount?: boolean;
}

export interface RegisterResult {
  isSuccess: boolean;
  vwfsToken?: string;
  partyId?: number;
  isAlreadyRegistered?: boolean;
  error?: string;
}

export interface TriggerMFAResult {
  isSuccess: boolean;
  isLocked?: boolean;
  error?: string;
}

export interface VerifyMFAResult {
  isSuccess: boolean;
  vwfsToken?: string;
  accountNumber?: string;
  loginAttemptCount?: number;
  isLocked?: boolean;
  error?: string;
}

/**
 * Verifies the customer's identity using their account number and SSN.
 * Maps to the portal's Step 2 (VerifyCustomer) — calls verifyCustomerRegistration.
 * On success returns partyId so the caller can proceed to MFA.
 * When sessionDetails is not provided falls back to a mock.
 */
export const register = async (
  accountNumber: string,
  ssn: string,
  sessionDetails?: SessionDetails,
): Promise<RegisterResult> => {
  if (sessionDetails) {
    try {
      Logger.info(
        `myVW Integration ===> register: request | ${JSON.stringify({ payload: { accountNumber, ssn: '[REDACTED]', correlationId: sessionDetails.correlationId, brand: sessionDetails.brand, country: sessionDetails.country } })}`,
        'Location ==> myVW service: register',
        sessionDetails,
      );
      const result = await verifyCustomerRegistration({ accountNumber, ssn, dob: '', vin: '', sessionDetails });
      Logger.info(
        `myVW Integration ===> register: response | ${JSON.stringify({ isSuccess: result?.data?.isSuccess, partyId: result?.data?.partyId, isPartyIdRegistered: result?.data?.isPartyIdRegistered, inMaintenance: result?.data?.inMaintenance })}`,
        'Location ==> myVW service: register',
        sessionDetails,
      );
      if (result?.data?.inMaintenance) {
        return { isSuccess: false, error: 'Service is temporarily unavailable. Please try again later.' };
      }
      if (result?.data?.isSuccess) {
        if (!result.data.partyId) {
          return { isSuccess: false, error: 'We couldn\'t find an account matching that information. Please check your details and try again.' };
        }
        if (result.data.isPartyIdRegistered) {
          return { isSuccess: false, isAlreadyRegistered: true };
        }
        return { isSuccess: true, partyId: result.data.partyId };
      }
      return { isSuccess: false, error: 'We were unable to verify your account. Please try again.' };
    } catch {
      return { isSuccess: false, error: 'We were unable to verify your account. Please try again.' };
    }
  }
  return { isSuccess: false, error: 'We were unable to verify your account. Please try again.' };
};

/**
 * Sends an MFA code to the user via the selected method.
 * When partyId + sessionDetails are provided the real generateMFA service is used;
 * otherwise falls back to a mock for local / test environments.
 */
export const triggerMFA = async (
  sessionToken: string,
  method: 'email' | 'authenticator',
  partyId?: number,
  sessionDetails?: SessionDetails,
): Promise<TriggerMFAResult> => {
  if (partyId && sessionDetails) {
    try {
      Logger.info(
        `myVW Integration ===> triggerMFA: request | ${JSON.stringify({ payload: { method, partyId, correlationId: sessionDetails.correlationId, brand: sessionDetails.brand, country: sessionDetails.country }, authorization: sessionToken })}`,
        'Location ==> myVW service: triggerMFA',
        sessionDetails,
      );
      const result = await generateMFA({ partyId, sessionDetails });
      Logger.info(
        `myVW Integration ===> triggerMFA: response | ${JSON.stringify({ isSuccess: result?.data?.isSuccess, errorCode: result?.data?.errors?.ErrorCode })}`,
        'Location ==> myVW service: triggerMFA',
        sessionDetails,
      );
      if (result?.data?.isSuccess) return { isSuccess: true };
      if (result?.data?.errors?.ErrorCode === 'badRequest_accountLocked') {
        return {
          isSuccess: false,
          isLocked: true,
          error: 'Your account has been locked due to too many failed attempts.',
        };
      }
      return { isSuccess: false, error: 'Failed to send MFA code. Please try again.' };
    } catch {
      return { isSuccess: false, error: 'Failed to send MFA code. Please try again.' };
    }
  }
  if (!sessionToken) return { isSuccess: false, error: 'Session expired. Please start again.' };
  return { isSuccess: false, error: 'Failed to send MFA code. Please try again.' };
};

/**
 * Verifies a 6-digit MFA code.
 * When partyId + sessionDetails are provided the real verifyCustomerMFA service is used;
 * otherwise falls back to a mock.
 */
export const verifyMFA = async (
  sessionToken: string,
  code: string,
  partyId?: number,
  rememberDevice?: boolean,
  sessionDetails?: SessionDetails,
  method: 'email' | 'authenticator' = 'email',
): Promise<VerifyMFAResult> => {
  if (partyId && sessionDetails) {
    try {
      Logger.info(
        `myVW Integration ===> verifyMFA: request | ${JSON.stringify({ payload: { method, partyId, rememberDevice, correlationId: sessionDetails.correlationId, brand: sessionDetails.brand, country: sessionDetails.country }, authorization: sessionToken })}`,
        'Location ==> myVW service: verifyMFA',
        sessionDetails,
      );
      const integratedSessionDetails = { ...sessionDetails, token: sessionToken };
      const result = method === 'authenticator'
        ? await verifyCustomerAuthenticatorCodeIntegrated({
          partyId,
          mfaCode: code,
          rememberDevice: rememberDevice ?? false,
          sessionDetails: integratedSessionDetails,
        })
        : await verifyCustomerMFAIntegrated({
          partyId,
          mfaCode: code,
          rememberDevice: rememberDevice ?? false,
          sessionDetails: integratedSessionDetails,
        });
      Logger.info(
        `myVW Integration ===> verifyMFA: response | ${JSON.stringify({ isVerified: result?.data?.isVerified, accountNumber: result?.data?.accountNumber, loginAttemptCount: result?.data?.loginAttemptCount, errors: result?.data?.errors })}`,
        'Location ==> myVW service: verifyMFA',
        sessionDetails,
      );
      if (result?.data?.isVerified) {
        return {
          isSuccess: true,
          vwfsToken: result?.data?.token ?? '',
          accountNumber: result?.data?.accountNumber ?? '',
        };
      }
      const errors = result?.data?.errors;
      const errorList = Array.isArray(errors) ? errors : errors ? [errors] : [];
      const isAccountLockedByErrorCode = errorList.some((error: any) => error?.ErrorCode === 'badRequest_accountLocked');

      if (isAccountLockedByErrorCode) {
        return { isSuccess: false, isLocked: true, error: 'Your account has been locked due to too many failed attempts.' };
      }
      if (result?.data?.loginAttemptCount >= 5) {
        return {
          isSuccess: false,
          isLocked: true,
          loginAttemptCount: result?.data?.loginAttemptCount,
          error: 'Your account has been locked due to too many failed attempts.',
        };
      }
      if (result?.data?.errors || result?.error) {
        return {
          isSuccess: false,
          loginAttemptCount: result?.data?.loginAttemptCount,
          error: 'Invalid or expired security code. Please try again.',
        };
      }
      return {
        isSuccess: false,
        loginAttemptCount: result?.data?.loginAttemptCount,
        error: 'Invalid or expired security code. Please try again.',
      };
    } catch {
      return { isSuccess: false, error: 'Unable to verify code. Please try again.' };      
    }
  }
  if (!sessionToken) {
    return { isSuccess: false, error: 'Session expired. Please start again.' };
  }
  if (!code || code.length < 6) {
    return { isSuccess: false, error: 'Invalid or expired security code. Please try again.' };
  }
  return { isSuccess: false, error: 'Unable to verify code. Please try again.' };
};

export interface UpdateEmailResult {
  isSuccess: boolean;
  loginAttemptCount?: number;
  isLocked?: boolean;
  error?: string;
}

export interface VerifyUpdateEmailIdentityResult {
  isSuccess: boolean;
  isVerified?: boolean;
  partyId?: number;
  loginAttemptCount?: number;
  isLocked?: boolean;
  error?: string;
}

export interface CheckAuthenticatorSecretResult {
  isSuccess: boolean;
  doesSecretExist?: boolean;
  error?: string;
}

export interface GenerateAuthenticatorSecretResult {
  isSuccess: boolean;
  secretURI?: string;
  secret?: string;
  error?: string;
}

/**
 * Verifies identity before update-email submission, matching legacy flow step 1.
 */
export const verifyUpdateEmailIdentity = async (
  username: string,
  accountNumber: string,
  ssn: string,
  sessionDetails?: SessionDetails,
): Promise<VerifyUpdateEmailIdentityResult> => {
  if (!username || !accountNumber || !ssn || !sessionDetails) {
    return { isSuccess: false, error: 'Unable to verify identity. Please try again.' };
  }

  try {
    Logger.info(
      `myVW Integration ===> verifyUpdateEmailIdentity: request | ${JSON.stringify({ payload: { username, accountNumber, ssn: '[REDACTED]', correlationId: sessionDetails?.correlationId, brand: sessionDetails?.brand, country: sessionDetails?.country } })}`,
      'Location ==> myVW service: verifyUpdateEmailIdentity',
      sessionDetails,
    );
    const result = await verifyEmail({
      username,
      accountNumber,
      ssn,
      dob: '',
      sessionDetails,
    });
    Logger.info(
      `myVW Integration ===> verifyUpdateEmailIdentity: response | ${JSON.stringify({ isSuccess: result?.data?.isSuccess, isVerified: result?.data?.isVerified, partyId: result?.data?.partyId, loginAttemptCount: result?.data?.loginAttemptCount, errorCode: result?.data?.errors?.ErrorCode })}`,
      'Location ==> myVW service: verifyUpdateEmailIdentity',
      sessionDetails,
    );

    if (result?.data?.isSuccess && result?.data?.isVerified) {
      return {
        isSuccess: true,
        isVerified: true,
        partyId: result?.data?.partyId,
      };
    }

    if (result?.data?.errors?.ErrorCode === 'badRequest_accountLocked') {
      return { isSuccess: false, isLocked: true, error: 'Your account has been locked due to too many failed attempts.' };
    }

    if (result?.data?.loginAttemptCount) {
      const loginAttemptCount = result.data.loginAttemptCount;
      return {
        isSuccess: false,
        loginAttemptCount,
        isLocked: loginAttemptCount >= 5,
        error: 'Invalid information. Please verify your details and try again.',
      };
    }

    return { isSuccess: false, error: 'Unable to verify identity. Please try again.' };
  } catch {
    return { isSuccess: false, error: 'Unable to verify identity. Please try again.' };
  }
};

/**
 * Updates the email address for the authenticated user.
 * This follows the same backend-driven rules as the legacy MFA update-email flow,
 * including login attempt count semantics and account lock handling.
 */
export const updateEmail = async (
  _sessionToken: string,
  newEmail: string,
  accountNumber: string,
  ssn: string,
  partyId?: number,
  sessionDetails?: SessionDetails,
): Promise<UpdateEmailResult> => {
  if (!partyId || !accountNumber || !ssn || !sessionDetails) {
    return { isSuccess: false, error: 'Unable to update email address. Please try again.' };
  }

  try {
    Logger.info(
      `myVW Integration ===> updateEmail: request | ${JSON.stringify({ payload: { newEmail, accountNumber, ssn: '[REDACTED]', partyId, correlationId: sessionDetails?.correlationId, brand: sessionDetails?.brand, country: sessionDetails?.country }, authorization: _sessionToken })}`,
      'Location ==> myVW service: updateEmail',
      sessionDetails,
    );
    const result = await updateEmailService({
      accountNumber,
      email: newEmail,
      ssn,
      partyId,
      sessionDetails,
    });
    Logger.info(
      `myVW Integration ===> updateEmail: response | ${JSON.stringify({ isSuccess: result?.data?.isSuccess, loginAttemptCount: result?.data?.loginAttemptCount, errorCode: result?.data?.errors?.ErrorCode })}`,
      'Location ==> myVW service: updateEmail',
      sessionDetails,
    );
    if (result?.data?.isSuccess) {
      return { isSuccess: true };
    }

    if (result?.data?.errors?.ErrorCode === 'badRequest_accountLocked') {
      return { isSuccess: false, isLocked: true, error: 'Your account has been locked due to too many failed attempts.' };
    }

    if (result?.data?.loginAttemptCount) {
      const loginAttemptCount = result.data.loginAttemptCount;
      return {
        isSuccess: false,
        loginAttemptCount,
        isLocked: loginAttemptCount >= 5,
        error: 'Invalid information. Please verify your details and try again.',
      };
    }

    return { isSuccess: false, error: 'Unable to update email address. Please try again.' };
  } catch (error) {
    return { isSuccess: false, error: 'Unable to update email address. Please try again.' };
  }
};

export interface CreateAccountResult {
  isSuccess: boolean;
  isUsernameConflict?: boolean;
  loginAuthenticatorCode?: string;
  error?: string;
}

/**
 * Creates the VWFS portal account (Step 4 in the portal register flow — consent).
 * Calls registerCustomer mutation with all collected registration data.
 * When sessionDetails is not provided falls back to a mock.
 */
export const createAccount = async (
  accountNumber: string,
  partyId: number,
  email: string,
  username: string,
  password: string,
  challengeQuestion: string,
  challengeAnswer: string,
  paperlessEnroll: boolean,
  sessionDetails?: SessionDetails,
): Promise<CreateAccountResult> => {
  if (sessionDetails) {
    try {
      Logger.info(
        `myVW Integration ===> createAccount: request | ${JSON.stringify({ payload: { accountNumber, partyId, email, username, password: '[REDACTED]', challengeQuestion, challengeAnswer: '[REDACTED]', paperlessEnroll, correlationId: sessionDetails.correlationId, brand: sessionDetails.brand, country: sessionDetails.country } })}`,
        'Location ==> myVW service: createAccount',
        sessionDetails,
      );
      const result = await registerCustomer({
        accountNumber,
        partyId,
        email,
        username,
        password,
        challengeQuestion,
        challengeAnswer,
        sessionDetails,
        paperlessEnroll
      });
      Logger.info(
        `myVW Integration ===> createAccount: response | ${JSON.stringify({ isSuccess: result?.data?.isSuccess, inMaintenance: result?.data?.inMaintenance, errorCode: result?.data?.errors?.ErrorCode })}`,
        'Location ==> myVW service: createAccount',
        sessionDetails,
      );
      if (result?.data?.inMaintenance) {
        return { isSuccess: false, error: 'Service is temporarily unavailable. Please try again later.' };
      }
      if (result?.data?.isSuccess) {
        const loginAuthenticatorCode =
          result?.data && "loginAuthenticatorCode" in result.data
            ? result.data.loginAuthenticatorCode
            : undefined;
        return {
          isSuccess: true,
          loginAuthenticatorCode,
        };
      }
      if (result?.data?.errors?.ErrorCode === 'conflict_Credsname') {
        return { isSuccess: false, isUsernameConflict: true };
      }
      return { isSuccess: false, error: 'We were unable to create your account. Please try again.' };
    } catch {
      return { isSuccess: false, error: 'We were unable to create your account. Please try again.' };
    }
  }
  return { isSuccess: false, error: 'We were unable to create your account. Please try again.' };
};

/**
 * Checks whether the customer's authenticator secret exists.
 * Uses the real GraphQL service when partyId + sessionDetails are available;
 * otherwise falls back to a mock.
 */
export const checkAuthenticatorSecretExists = async (
  _sessionToken: string,
  partyId?: number,
  sessionDetails?: SessionDetails,
  loginAuthenticatorCode?: string,
): Promise<CheckAuthenticatorSecretResult> => {
  if (partyId && sessionDetails) {
    try {
      Logger.info(
        `myVW Integration ===> checkAuthenticatorSecretExists: request | ${JSON.stringify({ payload: { partyId, loginAuthenticatorCode, correlationId: sessionDetails.correlationId, brand: sessionDetails.brand, country: sessionDetails.country }, authorization: _sessionToken })}`,
        'Location ==> myVW service: checkAuthenticatorSecretExists',
        sessionDetails,
      );
      const result = await checkCustomerAuthenticatorSecretExists({
        partyId,
        loginAuthenticatorCode: loginAuthenticatorCode ?? '',
        sessionDetails,
      });
      Logger.info(
        `myVW Integration ===> checkAuthenticatorSecretExists: response | ${JSON.stringify({ isSuccess: result?.data?.isSuccess, doesSecretExist: result?.data?.doesSecretExist, inMaintenance: result?.data?.inMaintenance })}`,
        'Location ==> myVW service: checkAuthenticatorSecretExists',
        sessionDetails,
      );
      if (result?.data?.inMaintenance) {
        return { isSuccess: false, error: 'Service is temporarily unavailable. Please try again later.' };
      }
      if (result?.data?.isSuccess) {
        return { isSuccess: true, doesSecretExist: result.data.doesSecretExist ?? false };
      }
      return { isSuccess: false, error: 'Unable to check authenticator status. Please try again.' };
    } catch {
      return { isSuccess: false, error: 'Unable to check authenticator status. Please try again.' };
    }
  }
  return { isSuccess: false, error: 'Unable to check authenticator status. Please try again.' };
};

/**
 * Generates a new authenticator secret for the customer.
 * Uses the real GraphQL service when partyId + sessionDetails are available;
 * otherwise falls back to a mock.
 */
export const generateAuthenticatorSecret = async (
  _sessionToken: string,
  partyId?: number,
  sessionDetails?: SessionDetails,
  loginAuthenticatorCode?: string,
): Promise<GenerateAuthenticatorSecretResult> => {
  if (partyId && sessionDetails) {
    try {
      Logger.info(
        `myVW Integration ===> generateAuthenticatorSecret: request | ${JSON.stringify({ payload: { partyId, loginAuthenticatorCode, correlationId: sessionDetails.correlationId, brand: sessionDetails.brand, country: sessionDetails.country }, authorization: _sessionToken })}`,
        'Location ==> myVW service: generateAuthenticatorSecret',
        sessionDetails,
      );
      const result = await generateAuthenticatorSecretService({
        partyId,
        loginAuthenticatorCode: loginAuthenticatorCode ?? '',
        sessionDetails,
      });
      Logger.info(
        `myVW Integration ===> generateAuthenticatorSecret: response | ${JSON.stringify({ isSuccess: result?.data?.isSuccess, secretURI: result?.data?.secretURI, inMaintenance: result?.data?.inMaintenance })}`,
        'Location ==> myVW service: generateAuthenticatorSecret',
        sessionDetails,
      );
      if (result?.data?.inMaintenance) {
        return { isSuccess: false, error: 'Service is temporarily unavailable. Please try again later.' };
      }
      if (result?.data?.isSuccess) {
        return { isSuccess: true, secretURI: result.data.secretURI, secret: result.data.secret };
      }
      return { isSuccess: false, error: 'Unable to generate authenticator secret. Please try again.' };
    } catch {
      return { isSuccess: false, error: 'Unable to generate authenticator secret. Please try again.' };
    }
  }
  return { isSuccess: false, error: 'Unable to generate authenticator secret. Please try again.' };
};
